import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { getAuthHeader } from "../../utils/authHeader";
import { toast } from "react-toastify";
import CustomToast from "../../components/misc/CustomToast";
import {
    SERVICE_UNAVAILABLE_REASON,
    VIDEO_SERVICE_UNAVAILABLE_MESSAGE,
} from "../../constants/video-service";
import LoadingScreen from "../LoadingScreen";
import ContentTabs from "../../components/content/ContentTabs";
import axios from "axios";
import UploadNavigationWarning from "../../components/misc/UploadNavigationWarning";
import { useUploadNavigationGuard } from "../../hooks/useUploadNavigationGuard";
import {
    AllListingMedia,
    ContentProps,
    PromotionsMedia,
    ResourceType,
} from "../../components/content/types";
import MediaCarousel from "../../components/misc/MediaCarousel";
import { CarouselApi } from "../../components/misc/Carousel";
import { CombinedMedia } from "../../components/listings/listing/types";
import { Assets } from "../../types/asset-type";
import { useIsMobile } from "../../hooks/useIsMobile";
import { PromotionType } from "../../components/promotions/types";
import { clampIndex } from "../../components/content/util";
import { useSocket } from "../../context/SocketContext";
import { ListingVideos } from "../../types/listingVideos";
import VideoConversionErrorDialog from "../../components/misc/VideoConversionError";

interface CarouselIndices {
    listing: {
        [address: string]: {
            images: number;
            videos: number;
        };
    };
    promotion: {
        [type in PromotionType]?: number;
    };
    resource: {
        [type in ResourceType]?: number;
    };
}

const ContentScreen: React.FC<ContentProps> = ({ mlsApproved }) => {
    const { socket, isAuthenticated } = useSocket();

    const subscribedVideosRef = useRef<Set<string>>(new Set());
    const videoProgressTimeoutRef = useRef<number | null>(null);

    const [listingMedia, setListingMedia] = useState<AllListingMedia[]>([]);
    const [promotions, setPromotions] = useState<PromotionsMedia | null>(null);
    const [resources, setResources] = useState<Assets[]>([]);

    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<
        "listing" | "promotion" | "resource"
    >("listing");
    const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
    const [carouselIndexMap, setCarouselIndexMap] = useState<{
        [id: string]: number;
    }>({});

    const [listingTabs, setListingTabs] = useState<string[]>([]);
    const [activeListingTab, setActiveListingTab] = useState<string>("");
    const [activeMediaTab, setActiveMediaTab] = useState<"images" | "videos">(
        "images"
    );

    const [activePromotionType, setActivePromotionType] =
        useState<PromotionType>(PromotionType.BuyerEducation);

    const [activeResourceType, setActiveResourceType] = useState<ResourceType>(
        ResourceType.Image
    );

    const [carouselIndices, setCarouselIndices] = useState<CarouselIndices>({
        listing: {},
        promotion: {},
        resource: {},
    });

    const [errorVideos, setErrorVideos] = useState<(Assets | ListingVideos)[]>(
        []
    );

    const [errorVideosDialogOpen, setErrorVideosDialogOpen] = useState(false);

    // Upload protection state
    const [activeUploads, setActiveUploads] = useState(0);
    const [showNavigationWarning, setShowNavigationWarning] = useState(false);

    // Handle active uploads callback with proper logging
    const handleActiveUploadsChange = useCallback((count: number) => {
        setActiveUploads(count);
    }, []);

    // Navigation guard to prevent accidental navigation during uploads
    const { confirmNavigation, cancelNavigation } = useUploadNavigationGuard({
        uploadCount: activeUploads,
        onNavigationAttempt: () => {
            setShowNavigationWarning(true);
        },
    });

    // Store active upload state for refresh detection
    const hasInitiallyMounted = useRef(false);
    useEffect(() => {
        
        if (activeUploads > 0) {
            sessionStorage.setItem('uploadsInterrupted', 'true');
        } else if (hasInitiallyMounted.current) {
            sessionStorage.removeItem('uploadsInterrupted');
            // If warning modal is open and uploads complete, close it and allow navigation
            if (showNavigationWarning) {
                setShowNavigationWarning(false);
            }
        }
        hasInitiallyMounted.current = true;
    }, [activeUploads, showNavigationWarning]);

    // Additional safety: close modal if activeUploads is 0 but modal is still open
    useEffect(() => {
        if (showNavigationWarning && activeUploads === 0) {
            setShowNavigationWarning(false);
        }
    }, [showNavigationWarning, activeUploads]);

    // Fetch Assets
    const fetchAssets = useCallback(async () => {
        const headers = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;

        try {
            const response = await axios.get(`${backendUrl}/promotions/media`, {
                headers,
            });

            if (response.status === 200) {
                setPromotions(response.data);
            } else {
                throw new Error(
                    "Failed to fetch assets. Please try again later."
                );
            }
        } catch (error: unknown) {
            console.error("Failed to fetch assets:", error);
        }
    }, []);

    // Fetch Listing Media
    const fetchListingMedia = useCallback(async () => {
        const headers = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;

        try {
            const response = await axios.get(`${backendUrl}/listing/media`, {
                headers,
            });

            if (response.status === 200) {
                setListingMedia(response.data);

                const tabs = response.data.map(
                    (listing: AllListingMedia) => listing.address
                );
                setListingTabs(tabs);
                setActiveListingTab(tabs[0] || "");
            } else {
                throw new Error(
                    "Failed to fetch listing media. Please try again later."
                );
            }
        } catch (error: unknown) {
            console.error("Failed to fetch listing media:", error);
        }
    }, []);

    // Fetch Resources
    const fetchResources = useCallback(async () => {
        const headers = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;

        try {
            const response = await axios.get(
                `${backendUrl}/assets/general-assets`,
                { headers }
            );

            if (response.status === 200) {
                setResources(response.data);
            } else {
                throw new Error(
                    "Failed to fetch resources. Please try again later."
                );
            }
        } catch (error: unknown) {
            console.error("Failed to fetch resources:", error);
        }
    }, []);

    // Aggregate Fetch Function
    const fetchAllData = useCallback(async () => {
        setLoading(true);
        try {
            await Promise.all([
                fetchAssets(),
                fetchListingMedia(),
                fetchResources(),
            ]);
        } catch (error) {
            console.error("Failed to fetch all data:", error);
            toast.error(
                <CustomToast
                    message="Failed to fetch media. Please try again later."
                    type="error"
                />,
                { autoClose: 3000 }
            );
        } finally {
            setLoading(false);
        }
    }, [fetchAssets, fetchListingMedia, fetchResources]);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    // Memoize allItems to prevent it from being recreated on every render
    const filteredMedia: CombinedMedia[] = useMemo(() => {
        if (activeTab === "listing") {
            const listing = listingMedia.find(
                (l) => l.address === activeListingTab
            );
            if (!listing) return [];

            if (activeMediaTab === "images") {
                return listing.images.map((img) => ({ ...img, type: "image" }));
            } else {
                return listing.videos.map((vid) => ({ ...vid, type: "video" }));
            }
        } else if (activeTab === "promotion") {
            if (!promotions?.assets) return [];
            return promotions.assets
                .filter((asset) => asset.assetType === activePromotionType)
                .map((asset) => ({ ...asset, type: "image" }));
        } else {
            return resources
                .filter((res) => res.assetType === activeResourceType)
                .map((res) => ({
                    ...res,
                    type: res.assetType === "Video" ? "video" : "image",
                }));
        }
    }, [
        activeTab,
        listingMedia,
        promotions,
        resources,
        activeListingTab,
        activeMediaTab,
        activePromotionType,
        activeResourceType,
    ]);

    useEffect(() => {
        const newMap: { [id: string]: number } = {};
        filteredMedia.forEach((media, index) => {
            newMap[media.id] = index;
        });
        setCarouselIndexMap(newMap);
    }, [filteredMedia]);

    useEffect(() => {
        if (!carouselApi) return;

        let desiredIndex = 0;

        if (activeTab === "listing") {
            const listingIndexObj = carouselIndices.listing[
                activeListingTab
            ] || { images: 0, videos: 0 };

            desiredIndex =
                activeMediaTab === "images"
                    ? listingIndexObj.images
                    : listingIndexObj.videos;
        } else if (activeTab === "promotion") {
            const promoIndexObj = carouselIndices.promotion || {};
            desiredIndex = promoIndexObj[activePromotionType] || 0;
        } else {
            const resourceIndexObj = carouselIndices.resource || {};
            desiredIndex = resourceIndexObj[activeResourceType] || 0;
        }

        const boundedIndex = clampIndex(desiredIndex, filteredMedia.length);
        carouselApi.scrollTo(boundedIndex);

        if (boundedIndex !== desiredIndex) {
            setCarouselIndices((prev) => {
                const next = { ...prev };
                if (activeTab === "listing") {
                    const listingIndexObj = next.listing[activeListingTab] || {
                        images: 0,
                        videos: 0,
                    };
                    if (activeMediaTab === "images") {
                        listingIndexObj.images = boundedIndex;
                    } else {
                        listingIndexObj.videos = boundedIndex;
                    }
                    next.listing[activeListingTab] = listingIndexObj;
                } else if (activeTab === "promotion") {
                    const promoIndexObj = { ...next.promotion };
                    promoIndexObj[activePromotionType] = boundedIndex;
                    next.promotion = promoIndexObj;
                } else {
                    const resourceIndexObj = { ...next.resource };
                    resourceIndexObj[activeResourceType] = boundedIndex;
                    next.resource = resourceIndexObj;
                }
                return next;
            });
        }
    }, [
        activeTab,
        activeListingTab,
        activeMediaTab,
        activePromotionType,
        activeResourceType,
        carouselApi,
        carouselIndices,
        filteredMedia.length,
    ]);

    const handleThumbnailClick = (newIndex: number) => {
        const boundedIndex = clampIndex(newIndex, filteredMedia.length);
        carouselApi?.scrollTo(boundedIndex);

        // Update carouselIndices based on activeTab
        setCarouselIndices((prev) => {
            const next = { ...prev };
            if (activeTab === "listing") {
                const listingIndexObj = next.listing[activeListingTab] || {
                    images: 0,
                    videos: 0,
                };
                if (activeMediaTab === "images") {
                    listingIndexObj.images = boundedIndex;
                } else {
                    listingIndexObj.videos = boundedIndex;
                }
                next.listing[activeListingTab] = listingIndexObj;
            } else if (activeTab === "promotion") {
                const promoIndexObj = { ...next.promotion };
                promoIndexObj[activePromotionType] = boundedIndex;
                next.promotion = promoIndexObj;
            } else {
                const resourceIndexObj = { ...next.resource };
                resourceIndexObj[activeResourceType] = boundedIndex;
                next.resource = resourceIndexObj;
            }
            return next;
        });
    };

    const isMobile = useIsMobile(1300);

    const stopVideoProgressWatcher = useCallback(() => {
        if (videoProgressTimeoutRef.current !== null) {
            clearTimeout(videoProgressTimeoutRef.current);
            videoProgressTimeoutRef.current = null;
        }
    }, []);

    const startVideoProgressWatcher = useCallback(() => {
        stopVideoProgressWatcher();

        const hasListingVideoProcessing = listingMedia.some((listing) =>
            listing.videos.some((video) => video.isProcessing)
        );

        const hasResourceVideoProcessing = resources.some(
            (res) => res.assetType === "Video" && res.isProcessing
        );

        if (hasListingVideoProcessing || hasResourceVideoProcessing) {
            videoProgressTimeoutRef.current = window.setTimeout(() => {
                // Re-fetch both sources
                Promise.all([fetchListingMedia(), fetchResources()]).finally(
                    () => {
                        startVideoProgressWatcher(); // re-arm
                    }
                );
            }, 30000); // 30 seconds
        }
    }, [
        listingMedia,
        resources,
        fetchListingMedia,
        fetchResources,
        stopVideoProgressWatcher,
    ]);

    useEffect(() => {
        const hasListingVideoProcessing = listingMedia.some((listing) =>
            listing.videos.some((video) => video.isProcessing)
        );

        const hasResourceVideoProcessing = resources.some(
            (res) => res.assetType === "Video" && res.isProcessing
        );

        if (hasListingVideoProcessing || hasResourceVideoProcessing) {
            startVideoProgressWatcher();
        } else {
            stopVideoProgressWatcher();
        }

        return stopVideoProgressWatcher;
    }, [
        listingMedia,
        resources,
        startVideoProgressWatcher,
        stopVideoProgressWatcher,
    ]);

    useEffect(() => {
        if (!socket || !isAuthenticated) {
            console.info(
                "Early return: socket or isAuthenticated is not defined"
            );
            return;
        }
        const currentSubscribedVideos = subscribedVideosRef.current;

        for (const listing of listingMedia) {
            for (const video of listing.videos) {
                if (
                    video.isProcessing &&
                    !currentSubscribedVideos.has(video.name)
                ) {
                    socket.emit("subscribeToVideoConvert", video.name);
                    currentSubscribedVideos.add(video.name);
                }
            }
        }

        for (const resource of resources) {
            if (
                resource.assetType === "Video" &&
                resource.isProcessing &&
                !currentSubscribedVideos.has(resource.name)
            ) {
                socket.emit("subscribeToVideoConvert", resource.name);
                currentSubscribedVideos.add(resource.name);
            }
        }

        const handleProgressUpdate = (data: {
            entityId: string;
            entityType: string;
            progress: string;
            reason?: string;
        }) => {
            if (data.entityType === "video-convert") {
                handleVideoProcessUpdate({
                    entityId: data.entityId,
                    entityType: data.entityType,
                    status: data.progress,
                    reason: data.reason,
                });
            }
        };

        const handleVideoProcessUpdate = (data: {
            entityId: string;
            entityType: "video-convert";
            status: string;
            reason?: string;
        }) => {
            if (data.entityType === "video-convert") {
                // MB-1396: the api's failure emitter sends JobStatus.FAILED
                // ("failed"); "error" is kept for the legacy step relay. When
                // the failure is the video service being unreachable, say so.
                const isFailure = ["error", "failed"].includes(
                    data.status.toLowerCase()
                );
                if (isFailure && data.reason === SERVICE_UNAVAILABLE_REASON) {
                    toast.error(
                        <CustomToast
                            message={VIDEO_SERVICE_UNAVAILABLE_MESSAGE}
                            type="error"
                        />,
                        { autoClose: 3000 }
                    );
                }
                setListingMedia((prev) =>
                    prev.map((listing) => {
                        const videoExists = listing.videos.find(
                            (vid) => vid.name === data.entityId
                        );
                        if (videoExists) {
                            if (isFailure) {
                                setErrorVideos((prev) => [
                                    ...prev,
                                    {
                                        ...videoExists,
                                        error: true,
                                        isProcessing: false,
                                    },
                                ]);
                                return {
                                    ...listing,
                                    videos: listing.videos.filter(
                                        (vid) => vid.name !== data.entityId
                                    ),
                                };
                            } else if (
                                data.status.toLowerCase() === "completed"
                            ) {
                                return {
                                    ...listing,
                                    videos: listing.videos.map((vid) =>
                                        vid.name === data.entityId
                                            ? {
                                                  ...vid,
                                                  isProcessing: false,
                                                  error: false,
                                              }
                                            : vid
                                    ),
                                };
                            }
                        }
                        return listing;
                    })
                );
                // Update shared media videos.
                setResources((prev) =>
                    prev.map((resource) => {
                        const videoExists =
                            resource.assetType === "Video" &&
                            resource.name === data.entityId;
                        if (videoExists) {
                            if (isFailure) {
                                setErrorVideos((prev) => [
                                    ...prev,
                                    {
                                        ...resource,
                                        error: true,
                                        isProcessing: false,
                                    },
                                ]);
                                return {
                                    ...resource,
                                    isProcessing: false,
                                    error: true,
                                };
                            } else if (
                                data.status.toLowerCase() === "completed"
                            ) {
                                return {
                                    ...resource,
                                    isProcessing: false,
                                    error: false,
                                };
                            }
                        }
                        return resource;
                    })
                );
            }
        };

        // Listen for progress updates
        socket.on("progressUpdate", handleProgressUpdate);

        // Cleanup function
        return () => {
            // Remove the progress update listener
            socket.off("progressUpdate", handleProgressUpdate);

            // Unsubscribe from listing media videos.
            for (const listing of listingMedia) {
                for (const video of listing.videos) {
                    if (video.isProcessing) {
                        socket.emit("unsubscribeFromVideoConvert", video.name);
                    }
                }
            }
            // Unsubscribe from shared media videos.
            for (const resource of resources) {
                if (resource.assetType === "Video" && resource.isProcessing) {
                    socket.emit("unsubscribeFromVideoConvert", resource.name);
                }
            }
        };
    }, [socket, isAuthenticated, listingMedia, resources]);

    const handleLeaveWithUploads = useCallback(() => {
        // Reset activeUploads first to prevent useEffect from re-adding the flag
        setActiveUploads(0);
        sessionStorage.removeItem('uploadsInterrupted');
        setShowNavigationWarning(false);
        confirmNavigation();
    }, [confirmNavigation]);

    const handleStayOnPage = useCallback(() => {
        setShowNavigationWarning(false);
        cancelNavigation();
    }, [cancelNavigation]);

    const handleDeleteImages = async () => {
        const errorListingVideoIds: {
            listingId: string;
            listingType: string;
            videoIds: string[];
        }[] = [];
        const errorResourceVideoIds: string[] = [];

        for (const video of errorVideos) {
            const listingVideo = listingMedia.find((listing) =>
                listing.videos.some(
                    (v) => v.id === video.id || v.url === video.url
                )
            );

            if (listingVideo) {
                const existing = errorListingVideoIds.find(
                    (entry) => entry.listingId === listingVideo.listingId
                );
                if (existing) {
                    existing.videoIds.push(video.id);
                } else {
                    errorListingVideoIds.push({
                        listingId: listingVideo.listingId,
                        listingType: listingVideo.listingType,
                        videoIds: [video.id],
                    });
                }
            }

            const resourceVideo = resources.find(
                (resource) =>
                    resource.id === video.id || resource.url === video.url
            );

            if (resourceVideo) {
                errorResourceVideoIds.push(video.id);
            }
        }

        const headers = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;

        try {
            for (const errorListingVideo of errorListingVideoIds) {
                await axios.delete(`${backendUrl}/listing/videos/`, {
                    headers: headers,
                    data: {
                        listingId: errorListingVideo.listingId,
                        listingType: errorListingVideo.listingType,
                        videos: errorListingVideo.videoIds,
                    },
                });
            }

            await axios.delete(`${backendUrl}/assets`, {
                headers,
                data: {
                    ids: errorResourceVideoIds,
                },
            });
        } catch (error) {
            console.error("Error deleting videos:", error);
        } finally {
            setErrorVideos([]);
            setErrorVideosDialogOpen(false);
        }
    };

    return (
        <>
            <UploadNavigationWarning
                isOpen={showNavigationWarning}
                onClose={handleStayOnPage}
                onLeave={handleLeaveWithUploads}
                onStay={handleStayOnPage}
            />
            <div
                className={`relative w-full mb-4 ${
                    isMobile ? "py-4 px-4" : "py-6 px-10"
                } overflow-y-visible`}
            >
                {loading && <LoadingScreen />}
                {errorVideos.length > 0 && (
                <VideoConversionErrorDialog
                    videos={errorVideos}
                    open={errorVideosDialogOpen}
                    onClose={() => {
                        // mark it as viewed and close the dialog
                        handleDeleteImages();
                        setErrorVideosDialogOpen(false);
                    }}
                />
            )}

            <div className="flex flex-col">
                <h1
                    className={`font-bold text-primary ${
                        isMobile ? "text-2xl mb-2" : "text-3xl mb-4"
                    }`}
                >
                    My Content
                </h1>
            </div>
            <div
                className={`${
                    isMobile ? "w-full mx-auto" : "w-[70%] mx-[15%]"
                }`}
            >
                <div className={`${isMobile ? "px-4" : "px-10"}`}>
                    <MediaCarousel
                        setCarouselApi={setCarouselApi}
                        combinedMedia={filteredMedia as CombinedMedia[]}
                        isMobile={isMobile}
                    />
                </div>
                <div className="flex flex-row items-center justify-center">
                    <ContentTabs
                        promotions={promotions}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        listingMedia={listingMedia}
                        listingTabs={listingTabs}
                        activeListingTab={activeListingTab}
                        setActiveListingTab={setActiveListingTab}
                        loading={loading}
                        mlsApproved={mlsApproved || false}
                        carouselApi={carouselApi}
                        fetchAssets={fetchAssets}
                        fetchListingMedia={fetchListingMedia}
                        setLoading={setLoading}
                        carouselIndexMap={carouselIndexMap}
                        resources={resources}
                        fetchResources={fetchResources}
                        isMobile={isMobile}
                        activeMediaTab={activeMediaTab}
                        setActiveMediaTab={setActiveMediaTab}
                        activePromotionType={activePromotionType}
                        setActivePromotionType={setActivePromotionType}
                        activeResourceType={activeResourceType}
                        setActiveResourceType={setActiveResourceType}
                        onThumbnailClick={handleThumbnailClick}
                        onActiveUploadsChange={handleActiveUploadsChange}
                    />
                </div>
            </div>
            </div>
        </>
    );
};

export default ContentScreen;
